import React, { memo, useMemo } from "react"

import {
  CRITERION_VALUE,
  type CriterionValue,
} from "@/constants/criterion-values"
import {
  reviewAssigneeMatches,
  useReviewAnnotationMode,
} from "@/human-feedback/annotations"
import { EVENTS, useEventDispatcher } from "@/inspection-events"
import { yesNoBadgeShapeClasses } from "@/metrics/metric-display"
import type { MetricReviewTranscriptContext } from "@/metrics/runtime"
import { Badge, Button } from "@coval/ui"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { isAutomaticExpectedBehaviorEnabled } from "../../configuration/utils/isExpectedBehaviorCompositeMetric"
import { useCriterionVoting } from "../../hooks/useCriterionVoting"
import {
  CRITERION_DISPLAY,
  CRITERION_VALUES,
  getCompositeCriterionDisplay,
  getCompositeReviewColumnClasses,
  getCriterionBadgeClass,
  getCriterionSelectedButtonClass,
  type CriterionResult,
} from "./composite-evaluation-types"
import { CompositeBehaviorCard } from "./CompositeBehaviorCard"
import { MessageReferenceChip } from "./MessageReferenceChip"
import { OtherReviewerLabels } from "./OtherReviewerLabels"
import { buildReferencedMessageAudioRegion } from "./referencedMessageAudioRegions"

interface CompositeCriteriaReviewProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  metadataCriteria?: string[] | null
  reportingMethod: string
  projectId?: string
  /** Blind review hides the machine verdict so the reviewer isn't anchored. */
  showAiResult?: boolean
  behaviorCards?: boolean
  /** The conversation the criteria were judged against, for naming and
   *  previewing the messages each criterion cites. */
  transcriptContext?: MetricReviewTranscriptContext
}

/** A reference under the pointer: the one message it names, and the whole set
 *  the criterion it belongs to cites. */
interface ReferencesHover {
  criterionMessageIndices: number[]
  messageIndex: number
}

const CriterionReferences: React.FC<{
  result: CriterionResult
  transcriptContext?: MetricReviewTranscriptContext
  onMessageSelect: (messageIndex: number) => void
  onReferencesHoverChange: (hover: ReferencesHover | null) => void
  compact?: boolean
}> = ({
  result,
  transcriptContext,
  onMessageSelect,
  onReferencesHoverChange,
  compact = false,
}) => {
  const messageIndices = result.matching_message_indices ?? []
  const inlineReferences = compact || isAutomaticExpectedBehaviorEnabled()
  return (
    <>
      {messageIndices.map((msgIndex, index) => (
        <React.Fragment key={msgIndex}>
          {!inlineReferences && index > 0 && (
            <span aria-hidden className="text-coval-text-muted">
              ·
            </span>
          )}
          <MessageReferenceChip
            message={transcriptContext?.messages[msgIndex]}
            messageIndex={msgIndex}
            participants={transcriptContext?.participants}
            onSelect={onMessageSelect}
            onPreviewOpenChange={(isOpen) =>
              onReferencesHoverChange(
                isOpen
                  ? {
                      criterionMessageIndices: messageIndices,
                      messageIndex: msgIndex,
                    }
                  : null,
              )
            }
          />
          {inlineReferences && " "}
        </React.Fragment>
      ))}
    </>
  )
}

/** Criterion text plus the AI's reasoning for it, in full: the reasoning is
 *  what a reviewer weighs their own vote against, so it is never cut off. */
const CriterionCell: React.FC<{
  result: CriterionResult
  showExplanation: boolean
  transcriptContext?: MetricReviewTranscriptContext
  onMessageSelect: (messageIndex: number) => void
  onReferencesHoverChange: (hover: ReferencesHover | null) => void
}> = ({
  result,
  showExplanation,
  transcriptContext,
  onMessageSelect,
  onReferencesHoverChange,
}) => {
  const messageIndices = result.matching_message_indices ?? []
  const inlineReferences = isAutomaticExpectedBehaviorEnabled()
  const references = (
    <CriterionReferences
      result={result}
      transcriptContext={transcriptContext}
      onMessageSelect={onMessageSelect}
      onReferencesHoverChange={onReferencesHoverChange}
    />
  )

  return (
    <div className="space-y-1">
      <p className="text-sm font-semibold leading-snug text-coval-text wrap-break-word">
        {result.criterion}
      </p>
      {showExplanation &&
        result.explanation &&
        (inlineReferences ? (
          <p className="text-sm whitespace-pre-wrap wrap-break-word text-coval-text-muted">
            {result.explanation}
            {messageIndices.length > 0 && " "}
            {references}
          </p>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="whitespace-pre-wrap wrap-break-word text-coval-text-muted">
              {result.explanation}
            </div>
            {messageIndices.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
                <span className="text-coval-text-muted">Referenced:</span>
                {references}
              </div>
            )}
          </div>
        ))}
    </div>
  )
}

/**
 * Per-criterion human review pairs the AI's verdict and evidence with the
 * reviewer's own vote, sharing the same voting and transcript interactions
 * between behavior cards and the legacy table.
 */
export const CompositeCriteriaReview: React.FC<CompositeCriteriaReviewProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      metricOutput,
      metadataCriteria,
      reportingMethod,
      projectId,
      showAiResult = true,
      behaviorCards = false,
      transcriptContext,
    }) => {
      const columns = getCompositeReviewColumnClasses()
      const { subjectEmail: userEmail } = useReviewAnnotationMode()
      const dispatch = useEventDispatcher()

      const { criteria, selectedValues, handleCriterionChange, isReadOnly } =
        useCriterionVoting({
          metricId,
          simulationOutputId,
          annotationsForMetric,
          metricOutput,
          metadataCriteria,
          reportingMethod,
          projectId,
          gateScoreUntilAllVoted: true,
        })

      const otherAnnotations = useMemo(
        () =>
          annotationsForMetric.filter(
            (annotation) =>
              !reviewAssigneeMatches(annotation.assignee, userEmail),
          ),
        [annotationsForMetric, userEmail],
      )

      const handleMessageSelect = (messageIndex: number) => {
        dispatch(EVENTS.MESSAGE_SELECTED, { messageIndex })
      }

      // The transcript otherwise lifts every message the whole metric cites,
      // which on a metric with several criteria is most of the conversation.
      // Naming one criterion's set narrows that to the evidence under the
      // pointer, and clearing it hands the metric-wide view back.
      const handleReferencesHoverChange = (hover: ReferencesHover | null) => {
        // While a reference is pointed at, the set travels without a metric id:
        // the viewer reads a hover that carries one as "highlight everything
        // this metric cites", which is the view being narrowed. On the way out
        // the id is what hands that wider view back — the card these references
        // sit in is still hovered and will not state it again by itself.
        dispatch(EVENTS.MESSAGES_HOVER, {
          messageIndices: hover?.criterionMessageIndices ?? null,
          ...(hover ? {} : { metricId }),
        })

        // A conversation that was spoken has the same evidence on the waveform,
        // so the reader hears where a criterion is pointing without first
        // finding the turns in the transcript. Only the turn under the pointer
        // is marked, in its own speaker's lane: a span of audio is a thing you
        // play, and the criterion's whole set would sweep up the minutes lying
        // between its turns and the other speaker along with them.
        //
        // It goes out as a detail hover either way: that is what tells the
        // player these regions are evidence pointed at inside this card rather
        // than the set a card the pointer merely crossed would state.
        const referencedRegion = hover
          ? buildReferencedMessageAudioRegion({
              message: transcriptContext?.messages[hover.messageIndex],
              messageIndex: hover.messageIndex,
              metricId,
            })
          : null
        dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
          regions: referencedRegion ? [referencedRegion] : null,
          source: "detail",
        })
      }

      const renderHumanReview = (index: number) => {
        const currentVal = (selectedValues?.[index] ?? "") as
          | CriterionValue
          | ""
        return (
          <div
            className={
              behaviorCards
                ? "flex flex-wrap items-start gap-1.5"
                : "flex flex-col items-stretch gap-1.5 @md/card:flex-row @md/card:items-start"
            }
          >
            {CRITERION_VALUES.map((val) => (
              <Button
                key={val}
                variant="secondary"
                disabled={isReadOnly}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCriterionChange(index, val)
                }}
                aria-label={`Mark criterion ${index + 1} as ${getCompositeCriterionDisplay(val).label}`}
                className={cn(
                  behaviorCards
                    ? "transition-colors duration-150"
                    : "w-full transition-colors duration-150 @md/card:w-auto",
                  currentVal === val
                    ? getCriterionSelectedButtonClass(val)
                    : // Unselected options keep the stock secondary
                      // surface and border; once a vote exists the
                      // others recede by label color, not opacity,
                      // which would wash the border out too.
                      currentVal && "text-coval-text-muted",
                )}
              >
                {getCompositeCriterionDisplay(val).label}
              </Button>
            ))}
            <OtherReviewerLabels
              annotations={otherAnnotations}
              subject={`criterion ${index + 1}`}
              className={behaviorCards ? undefined : "w-full @md/card:w-auto"}
              renderValue={(otherAnnotation) => {
                const otherVal =
                  otherAnnotation.ground_truth_set_value?.[index] ?? ""
                const display = getCompositeCriterionDisplay(
                  otherVal as CriterionValue,
                )
                return display ? (
                  <Badge
                    variant={
                      otherVal === CRITERION_VALUE.MET
                        ? "success"
                        : otherVal === CRITERION_VALUE.NOT_MET
                          ? "danger"
                          : "default"
                    }
                  >
                    {display.label}
                  </Badge>
                ) : null
              }}
            />
          </div>
        )
      }

      const renderAiVerdict = (criterion: CriterionResult) => {
        const aiLabel =
          criterion.result === CRITERION_VALUE.NOT_MET
            ? "Not met"
            : CRITERION_DISPLAY[criterion.result]?.label
        return aiLabel ? (
          <span
            className={`${yesNoBadgeShapeClasses} ${getCriterionBadgeClass(criterion.result)}`}
          >
            {aiLabel}
          </span>
        ) : null
      }

      if (criteria.length === 0) {
        return null
      }

      if (behaviorCards) {
        return (
          <div className="space-y-3">
            {criteria.map((criterion, index) => (
              <CompositeBehaviorCard
                key={index}
                criterion={criterion}
                index={index}
                showAiResult={showAiResult}
                aiVerdict={renderAiVerdict(criterion)}
                humanReview={renderHumanReview(index)}
                references={
                  <CriterionReferences
                    compact
                    result={criterion}
                    transcriptContext={transcriptContext}
                    onMessageSelect={handleMessageSelect}
                    onReferencesHoverChange={handleReferencesHoverChange}
                  />
                }
              />
            ))}
          </div>
        )
      }

      return (
        <div className="w-full overflow-x-auto rounded-md border border-coval-border">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-coval-border text-left text-sm">
                <th className="border-r border-coval-border px-3 py-2 font-semibold text-coval-text">
                  Criteria
                </th>
                {/* The AI's verdict sits between the criterion it judged and
                    the reviewer's own vote, so the two verdicts read side by
                    side. */}
                {showAiResult && (
                  <th
                    className={`${columns.ai} whitespace-nowrap border-r border-coval-border px-3 py-2 text-left font-semibold text-coval-text`}
                  >
                    {isAutomaticExpectedBehaviorEnabled() ? "AI Judge" : "AI"}
                  </th>
                )}
                <th
                  className={`${columns.human} whitespace-nowrap px-3 py-2 text-left font-semibold text-coval-text`}
                >
                  Human
                </th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((criterion, index) => {
                return (
                  <tr
                    key={index}
                    className="border-b border-coval-border align-top last:border-b-0"
                  >
                    <td className="border-r border-coval-border px-3 py-3">
                      <CriterionCell
                        result={criterion}
                        showExplanation={showAiResult}
                        transcriptContext={transcriptContext}
                        onMessageSelect={handleMessageSelect}
                        onReferencesHoverChange={handleReferencesHoverChange}
                      />
                    </td>
                    {showAiResult && (
                      <td
                        className={`${columns.ai} whitespace-nowrap border-r border-coval-border px-3 py-3`}
                      >
                        {renderAiVerdict(criterion)}
                      </td>
                    )}
                    <td className={`${columns.human} px-3 py-3`}>
                      {/* The vote buttons stack while the card is narrow, so
                          the Human column can stay slim enough to leave the
                          criteria text a readable measure. */}
                      {renderHumanReview(index)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    },
  )
CompositeCriteriaReview.displayName = "CompositeCriteriaReview"
